import type { AssistantActionStatus, AssistantActionTool, AssistantCardKind, AssistantGapKind, AssistantOutcome, AssistantTopic } from "@teamna/shared";
import type { BadgeTone } from "@teamna/ui/components/index.ts";

/**
 * The words of the AI Assistant review in Control Center. Desk never shows these enums, so they live
 * here, next to the two review screens.
 */

export const TOPIC_WORDS: Record<AssistantTopic, string> = {
  payments: "Payments",
  sessions: "Sessions",
  attendance: "Attendance",
  members: "Members",
  coaches: "Coaches",
  messages: "Messages",
  packages: "Packages",
  numbers: "Numbers",
  how_to: "How to use Teamna",
  out_of_scope: "Out of scope",
  other: "Other",
};

export const OUTCOME_LOOK: Record<AssistantOutcome, { label: string; tone: BadgeTone }> = {
  answered: { label: "Answered", tone: "confirmed" },
  asked_back: { label: "Asked back", tone: "quiet" },
  refused: { label: "Refused", tone: "late" },
  limit: { label: "Limit", tone: "late" },
  error: { label: "Error", tone: "cancelled" },
  no_tool: { label: "No tool", tone: "late" },
};

export const GAP_KIND_WORDS: Record<AssistantGapKind, string> = {
  count: "How many",
  list: "Who or which",
  compare: "Compare",
  trend: "Over time",
  action: "A change",
  other: "Other",
};

export const PLAN_WORDS: Record<AssistantActionStatus, string> = {
  proposed: "On the table",
  replaced: "Replaced",
  confirmed: "Confirmed",
  stopped: "Stopped",
  stale: "Stale",
  failed: "Failed",
};

export const CARD_KIND_WORDS: Record<AssistantCardKind, string> = {
  unpaid_links: "Suggestion: unpaid links",
  session_no_coach: "Suggestion: session with no coach",
  missed_in_row: "Suggestion: missed sessions in a row",
  class_under_half: "Suggestion: classes under half full",
  pack_ending_unbooked: "Suggestion: packs that end, nothing booked",
  intro_no_next_pack: "Suggestion: intro clients with no next pack",
};

export const TOOL_WORDS: Record<AssistantActionTool, string> = {
  session_set_coach: "Set the coach",
  session_move: "Move a session",
  session_cancel: "Cancel a session",
  payment_reminder_send: "Send a payment reminder",
  family_message_send: "Send a message to a family",
  renewal_send: "Send the renewal",
};

export const ROLE_WORDS: Record<"owner" | "admin" | "other", string> = {
  owner: "Owner",
  admin: "Admin",
  other: "Other",
};

/**
 * Millionths of a US dollar as "$0.42". Under one cent it keeps four places ("$0.0042"), so one
 * cheap answer does not read as free.
 */
export function formatUsd(micros: number): string {
  const dollars = micros / 1_000_000;
  if (micros > 0 && micros < 10_000) return `$${dollars.toFixed(4)}`;
  return `$${dollars.toFixed(2)}`;
}
